import { useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';

import { Modal } from '../Modal.jsx';
import { Stack } from '../Stack.jsx';

/** Asks which transition slot a dragged connection should fill (branch/decision). */
export function ConnectSlotModal({
  source,
  target,
  targetLabel,
  step,
  enumOptions,
  onCancel,
  onApply,
}: {
  source: string;
  target: string;
  /** Friendly destination label when the editor has it available. */
  targetLabel?: string;
  step: v4.BranchStepSpec | v4.DecisionStepSpec;
  enumOptions?: string[];
  onCancel: () => void;
  onApply: (patch: Partial<v4.StepSpec>) => void;
}) {
  const [caseValue, setCaseValue] = useState('');
  const [routeType, setRouteType] = useState<'exact' | 'fallback'>('exact');
  const targetName = targetLabel || target;
  const targetReference =
    targetLabel && targetLabel !== target
      ? `${targetLabel} (${target})`
      : target;

  if (step.kind === 'decision') {
    return (
      <Modal
        title={`Connect ${source} → ${targetName}`}
        onClose={onCancel}
        actions={<Button onClick={onCancel}>Cancel</Button>}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Which outcome leads to <code>{targetReference}</code>?
        </Typography>
        <Stack direction="row" spacing={1}>
          {step.actions.map((a) => (
            <Button
              key={a.label}
              variant="contained"
              onClick={() => onApply({ [a.label]: target })}
            >
              {a.label}
            </Button>
          ))}
        </Stack>
      </Modal>
    );
  }

  const fieldName = step.selectField || 'the selected data';
  const hasEnumOptions = Boolean(enumOptions?.length);
  const hasConfiguredValue = (value: string) =>
    Object.keys(step.cases).some(
      (configuredValue) => configuredValue.trim() === value.trim(),
    );
  const remainingEnumOptions = [...new Set(enumOptions ?? [])].filter(
    (option) => option.trim().length > 0 && !hasConfiguredValue(option),
  );
  const exactValue = hasEnumOptions ? caseValue : caseValue.trim();
  const duplicateExactValue =
    exactValue.length > 0 && hasConfiguredValue(exactValue);
  const canAddExactValue =
    exactValue.length > 0 &&
    !duplicateExactValue &&
    (!hasEnumOptions || remainingEnumOptions.includes(exactValue));
  const hasFallbackAlready = step.default === target;
  const canApply =
    routeType === 'exact' ? canAddExactValue : !hasFallbackAlready;

  const applyRoute = () => {
    if (routeType === 'fallback') {
      if (!hasFallbackAlready) {
        onApply({ default: target });
      }
      return;
    }

    if (!canAddExactValue) {
      return;
    }

    onApply({
      cases: {
        ...step.cases,
        [exactValue]: target,
      },
    });
  };

  let exactHelp =
    'This matches the value exactly, including spelling and punctuation.';
  if (hasEnumOptions) {
    exactHelp =
      remainingEnumOptions.length > 0
        ? 'Choose one value that does not have its own route yet.'
        : 'Every available value already has its own route.';
  }

  const routePreview = (() => {
    if (routeType === 'fallback') {
      return (
        <>
          For any other value of{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            {fieldName}
          </Box>
          , continue with{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            {targetReference}
          </Box>
          .
        </>
      );
    }

    if (exactValue) {
      return (
        <>
          When{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            {fieldName}
          </Box>{' '}
          is exactly{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            {exactValue}
          </Box>
          , continue with{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            {targetReference}
          </Box>
          .
        </>
      );
    }

    return <>Choose a value to preview this route.</>;
  })();

  return (
    <Modal
      title={`Connect ${source} → ${targetName}`}
      onClose={onCancel}
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" disabled={!canApply} onClick={applyRoute}>
            {routeType === 'fallback' ? 'Use as fallback' : 'Add route'}
          </Button>
        </>
      }
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Use{' '}
          <Box component="code" sx={{ fontFamily: 'monospace' }}>
            {targetReference}
          </Box>{' '}
          when…
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          Choose the condition that should send the workflow to this step.
        </Typography>
      </Box>

      <RadioGroup
        value={routeType}
        onChange={(event) =>
          setRouteType(event.target.value as 'exact' | 'fallback')
        }
        aria-label="When to use this step"
      >
        <Stack spacing={1}>
          <Box
            sx={{
              border: 1,
              borderColor: routeType === 'exact' ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              bgcolor:
                routeType === 'exact' ? 'action.selected' : 'transparent',
              overflow: 'hidden',
            }}
          >
            <FormControlLabel
              value="exact"
              control={<Radio />}
              sx={{
                alignItems: 'flex-start',
                m: 0,
                px: 1.25,
                pt: 1.25,
                '& .MuiFormControlLabel-label': { minWidth: 0 },
              }}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    For a specific value
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    When{' '}
                    <Box component="code" sx={{ fontFamily: 'monospace' }}>
                      {fieldName}
                    </Box>{' '}
                    is exactly a value you choose below.
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ px: 1.5, pb: 1.5, pl: 5.25 }}>
              {hasEnumOptions ? (
                <TextField
                  fullWidth
                  select
                  label={`When ${fieldName} is exactly`}
                  value={caseValue}
                  disabled={
                    routeType !== 'exact' || remainingEnumOptions.length === 0
                  }
                  onFocus={() => setRouteType('exact')}
                  onChange={(event) => setCaseValue(event.target.value)}
                  helperText={exactHelp}
                >
                  <MenuItem value="">Choose a value</MenuItem>
                  {remainingEnumOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  autoFocus
                  label={`When ${fieldName} is exactly`}
                  placeholder="For example: rate_request"
                  value={caseValue}
                  disabled={routeType !== 'exact'}
                  onFocus={() => setRouteType('exact')}
                  onChange={(event) => setCaseValue(event.target.value)}
                  error={duplicateExactValue}
                  helperText={
                    duplicateExactValue
                      ? 'This value already has a route.'
                      : exactHelp
                  }
                />
              )}
            </Box>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor:
                routeType === 'fallback' ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              bgcolor:
                routeType === 'fallback' ? 'action.selected' : 'transparent',
            }}
          >
            <FormControlLabel
              value="fallback"
              control={<Radio />}
              sx={{
                alignItems: 'flex-start',
                m: 0,
                p: 1.25,
                '& .MuiFormControlLabel-label': { minWidth: 0 },
              }}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    For any other value
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Continue here when none of the specific routes above match.
                  </Typography>
                  {step.default && step.default !== target && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'warning.main',
                        display: 'block',
                        mt: 0.5,
                      }}
                    >
                      This will replace the current fallback to{' '}
                      <Box component="code" sx={{ fontFamily: 'monospace' }}>
                        {step.default}
                      </Box>
                      .
                    </Typography>
                  )}
                  {hasFallbackAlready && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mt: 0.5,
                      }}
                    >
                      This step is already used as the fallback.
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
        </Stack>
      </RadioGroup>

      <Box
        aria-live="polite"
        sx={{
          borderLeft: 3,
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
          borderRadius: 1,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          ROUTE PREVIEW
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {routePreview}
        </Typography>
      </Box>
    </Modal>
  );
}
